import React, { useEffect, useState } from "react";

import { cls } from "app/utils";
import UserAvatar from "app/components/UserAvatar";
import { FamilyMember } from "app/routes/Account/FamilyEdit/types";
import { getFamilyRoster } from "app/routes/Sessions/familyRosterCache";

import styles from "./styles.module.scss";

interface Props {
  participantAthleteId?: string | null;
}

/**
 * Read-only chip for the booking-flow header (CN-1323): shows which family
 * member the booking is for. Resolves the member from the family roster by
 * participantAthleteId; renders nothing for self-bookings or before the roster
 * loads. The participant is locked in by the picker modal — no dropdown here.
 */
const SelectedParticipantChip = ({ participantAthleteId }: Props) => {
  const [member, setMember] = useState<FamilyMember | null>(null);

  useEffect(() => {
    if (!participantAthleteId) {
      setMember(null);
      return undefined;
    }
    let active = true;
    // CFTB-3301: read through the shared roster cache instead of firing a fresh
    // `family_memberships` GET per chip.
    getFamilyRoster()
      .then((list) => {
        if (!active) {
          return;
        }
        setMember(list.find((m) => m.userId === participantAthleteId) ?? null);
      })
      .catch(() => {
        // No roster available — leave the chip hidden.
      });
    return () => {
      active = false;
    };
  }, [participantAthleteId]);

  if (!member) {
    return null;
  }

  const name = `${member.firstName} ${member.lastName}`.trim();

  return (
    <div className={styles.chip}>
      <UserAvatar
        sizeWidth={20}
        sizeHeight={20}
        altName={name}
        srcName={member.avatar ?? "default"}
        userName={name}
        className={cls("avatar", styles.chip_avatar)}
      />
      <span className={styles.chip_label}>{name}</span>
    </div>
  );
};

export default SelectedParticipantChip;
